import { ComponentProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import styles from "@components/v1/cards/ArticleCard.module.css";
import DateAndTimeStamp from "@components/v1/dates/DateAndTimeStamp";
import GatherTag from "@components/v1/labels/GatherTag";
import Tag from "@components/v1/labels/Tag";
import PinToggle from "@components/v1/PinToggle";
import BodyCopy from "@components/v1/typography/BodyCopy";
import InternalLink from "@components/v1/typography/InternalLink";
import Button from "@components/v2/buttons/Button";
import Icon from "@components/v2/icons/Icon";
import useMyExpert from "@hooks/useMyExpert";
import { ArticleContentTypeEnum, ArticleForCardFragment, PinResourceTypeEnum } from "@typing/Generated";
import { articleLength } from "@utils/articleUtils";
import { articleHasVideo } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  article: ArticleForCardFragment;
  border?: "danger-300";
  goal?: ComponentProps<typeof PinToggle>["goal"];
  journeyId: string | null;
  onRemove?: (articleId: string) => void;
};

const ArticleCard = ({ article, border, goal, journeyId, onRemove }: Props) => {
  const intl = useIntl();

  const myExpert = useMyExpert();

  const articleRoute = journeyRoute({
    journeyId: journeyId ?? "",
    linkAfterJourney: `/articles/${article.id}`,
    myExpert
  });

  return (
    <div className="relative">
      <InternalLink
        data-test="article-card"
        expand
        linkEventCreateVariables={{ journeyId }}
        stylized={false}
        to={articleRoute}
      >
        {article.thumbnailUrl && (
          <div className={`${styles.container} ${border ? styles.editModeBorder : ""}`}>
            <img alt={article.title} className={styles.image} src={article.thumbnailUrl} />

            <div>
              <div className={styles.toggles}>
                {article.contentType === ArticleContentTypeEnum.WEBINAR && (
                  <>
                    {article.webinarRegistration ? (
                      <Tag color="pink" text={intl.formatMessage({ id: "pages.article.registered" })} />
                    ) : article.hostedByGather ? (
                      <GatherTag />
                    ) : (
                      <Tag color="gray-300" text={intl.formatMessage({ id: "pages.article.liveEvent" })} />
                    )}
                  </>
                )}

                {articleHasVideo(article) && (
                  <Icon className={styles.videoIcon} color="primary-300" icon="videoCircle" size="large" />
                )}
              </div>

              <div className="padding-8-end padding-8-start padding-8-top">
                <BodyCopy bold className={styles.truncate} color="primary">
                  {article.title}
                </BodyCopy>
              </div>
            </div>
          </div>
        )}
      </InternalLink>
      <div className="flex items-center gap-sm padding-8-end padding-8-start padding-8-top">
        <div>
          {journeyId && (
            <PinToggle
              color="gray-200"
              goal={goal}
              journeyId={journeyId}
              resourceId={article.id}
              resourceType={PinResourceTypeEnum.ARTICLE}
            />
          )}
        </div>
        <div>
          {article.contentType === ArticleContentTypeEnum.WEBINAR && !article.webinarCompleted ? (
            <BodyCopy bold color="primary-300" size="small">
              <DateAndTimeStamp showWeekday value={article.webinarStartTime} />
            </BodyCopy>
          ) : (
            <BodyCopy color="gray-300" size="small">
              {articleLength(article, intl)}
            </BodyCopy>
          )}
        </div>
      </div>
      {onRemove && journeyId && (
        <div className="padding-8-top">
          <Button
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onRemove(article.id);
            }}
          >
            <FormattedMessage id="dictionary.remove" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ArticleCard;
